import React, { useEffect, useRef, useState } from "react";
import {
  getClusterFile,
  getClusterFileNew,
  getInstitutionCelexFile,
  getInstitutionCelexFileNew,
  getMeasureReportDoc,
  getNationalMeasureReportingByQuarter,
  getNationalMeasuresCount,
  getNationalMeasuresForReporting,
  getRegulationReportCounts,
  lockEditingStatus,
  updateNatMeasureOJ,
  updateRealisationDate,
  updateRealisationNote,
} from "../../../server/server";
import { notif, quarterYearEncoder } from "../../utils/util";
import Counter from "./subComponents/Counter";
import {
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Stack,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import FilterForm from "./subComponents/FilterForm";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import EditRealisationModal from "./subComponents/EditRealisationModal";
import { useUser } from "../../../auth/UseUser";
import NegDocs from "../../register/srceens/editRegScreens/NegDocs";
import NegDocsModal from "./subComponents/NegDocsModal";
import StrategicDocsModal from "./subComponents/StrategicDocsModal";
import PirvDocumentModal from "./subComponents/PirvDocumentModal";
import EditNoteModal from "./subComponents/EditNoteModal";
import OjModal from "./subComponents/OjModal";
import EuRelationModal from "./subComponents/EuRelationModal";
import CustomTableFooter from "../../../common/CustomTableFooter";
import HelpButton from "../../../common/HelpButton";
import HelpPageModal from "../../modals/HelpPageModal";
import { useLocation, useOutletContext } from "react-router-dom";
import ShowPirvDocumentModal from "./subComponents/ShowPirvDocumentModal";
import { FaFileWord, FaPencilAlt } from "react-icons/fa";
import download from "downloadjs";

const NationalMeasuresReport = () => {
  const { user } = useUser();
  const isAdmin = user?.roleView?.name.includes("Администратор");
  const isCoordinator = user?.roleView?.name.includes("Координатор");
  const isDataEntryOrSecretary =
    user?.roleView?.name.includes("Уносилац података") ||
    user?.roleView?.name.includes("Секретар");
  const location = useLocation();
  const reportId = location.pathname.split("/")[3];

  const [counts, setCounts] = useState<{
    quarter?: string;
    totalPlannedCount: number;
    realisedCount: number;
    previousQuarterUnrealisedCount: number;
    totalUnrealisedCount: number;
  } | null>(null);
  const [countsForFilter, setCountsForFilter] = useState<any>({
    totalPlannedCount: 0,
    unrealisedCount: 0,
  });
  const [term, setTerm] = useState<string>("");
  const [defaultInstitution, setDefaultInstitution] = useState<any | null>(
    user?.institution && user?.roleView?.name !== "Администратор"
      ? user.institution
      : null,
  );
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowCountState, setRowCountState] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [registers, setRegisters] = useState<any[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [status, setStatus] = useState<"planned" | "unrealised" | "">(
    "planned",
  );
  const [itemToChange, setItemToChange] = useState<any>(null);
  const [showEditRealisationModal, setShowEditRealisationModal] =
    useState<boolean>(false);
  const [showNegDocumentLinkModal, setShowNegDocumentLinkModal] =
    useState<boolean>(false);
  const [showStrategicDocumentLinkModal, setShowStrategicDocumentLinkModal] =
    useState<boolean>(false);
  const [showPirvDocumentLinkModal, setShowPirvDocumentLinkModal] =
    useState<boolean>(false);
  const [showEditNoteModal, setShowEditNoteModal] = useState<boolean>(false);
  const [showOjModal, setShowOjModal] = useState<boolean>(false);
  const [showEURelationModal, setShowEURelationModal] =
    useState<boolean>(false);
  const [tempRealisation, setTempRealisation] = useState<any>(null);
  const [helpModal, setHelpModal] = useState<boolean>(false);
  const scrollRef = useRef<any>(null);
  const [noteData, setNoteData] = useState<any>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<string>("");
  const [editDisabled, setEditDisabled] = useState<boolean>(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [selectedReportType, setSelectedReportType] =
    useState<string>("cluster");

  useEffect(() => {
    if (scrollRef?.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [page]);

  const renderTruncated = (value: any) => {
    const v = value === null || value === undefined ? "" : value;
    const text = typeof v === "object" ? JSON.stringify(v) : String(v);
    return (
      <Tooltip title={text} className="w-fit">
        <div
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: "1.2em",
            maxHeight: "2.4em",
          }}
        >
          {text}
        </div>
      </Tooltip>
    );
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  useEffect(() => {
    fetchRegisters();
  }, [page, pageSize, term, status, defaultInstitution, sortModel, reportId]);

  const handleInstitutionChange = (event: any, newValue: any) => {
    setDefaultInstitution(newValue as any);
  };

  const fetchCounts = async () => {
    const resp = await getRegulationReportCounts(
      reportId,
      user?.roleView?.name,
    );

    if (resp.status === 200) {
      setCounts(resp.data ?? resp.data);
    } else {
      setCounts({
        quarter: "",
        totalPlannedCount: 0,
        realisedCount: 0,
        previousQuarterUnrealisedCount: 0,
        totalUnrealisedCount: 0,
      });
      notif("Грешка при добивању података о прописима", "error");
    }
  };

  const handleSaveNote = async ({ id, note, reportId }: any) => {
    const resp = await updateRealisationNote({
      id: parseInt(id),
      note: note,
      reportId: reportId,
    });

    if (resp.status === 201) {
      notif("Напомена је успешно сачувана", "success");
      setShowEditNoteModal(false);
      setItemToChange(null);
      fetchRegisters();
    } else {
      alert("Грешка при чувању измена.");
    }
  };

  const handleCellClick = (params: any) => {
    if (editDisabled) {
      notif("Извештавање је закључано за уређивање.", "warning");
      return;
    }

    const clickedItem = params.row;
    const itemCreatedBy = clickedItem?.userId;
    const editableFields = new Set([
      "status",
      "natMeasureAdopByGovDate",
      "vezaEvropskiCelex",
      "vezaPregovaracki",
      "vezaStrateski",
      "vezaPirv",
      "note",
      "oj",
    ]);

    if (!isAdmin && !isCoordinator && editableFields.has(params.field)) {
      if (isDataEntryOrSecretary) {
        if (itemCreatedBy !== user?.id) {
          notif(
            `Не можете уређивати овај пропис, нисте аутор уноса.`,
            "warning",
          );
          return;
        }
      } else {
        if (itemCreatedBy !== user?.id) {
          notif(
            `Не можете уређивати овај пропис: немате довољно права (само администратор/координатор/аутор могу да уређују).`,
            "warning",
          );
          return;
        }
      }
    }

    switch (params.field) {
      case "status":
      case "natMeasureAdopByGovDate":
        setItemToChange(clickedItem);
        setTimeout(() => {
          setShowEditRealisationModal(true);
        }, 0);

        break;
      case "natMeasureStrategyDocs":
        setItemToChange(clickedItem);
        setShowStrategicDocumentLinkModal(true);

        break;
      case "natMeasureNegotiationDocs":
        setItemToChange(clickedItem);
        setShowNegDocumentLinkModal(true);
        break;
      case "natMeasurePirvId":
        setItemToChange(clickedItem);
        setShowPirvDocumentLinkModal(true);
        break;
      case "natMeasureNote":
        setItemToChange(clickedItem);
        setShowEditNoteModal(true);
        break;
      case "natMeasureOj":
        if (clickedItem.status !== "Реализовано") {
          notif(
            "Није могуће уређивати Службени гласник пре реализације прописа.",
            "warning",
          );
          return;
        } else {
          setItemToChange(clickedItem);
          setShowOjModal(true);
          break;
        }
      case "natMeasureCelex":
        setItemToChange(clickedItem);
        setShowEURelationModal(true);
        break;
      default:
        break;
    }
  };

  const columns: GridColDef[] = [
    {
      field: "natMeasureTitle",
      headerName: "Назив прописа",
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        renderTruncated(params.row?.natMeasureTitle ?? params.value),
    },
    {
      field: "natMeasureInstitutionAbbreviation",
      headerName: "Првонaдлежна институција",
      width: 140,
      renderCell: (params) => (
        <Tooltip title={params.row?.natMeasureInstitutionName ?? "-"}>
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              lineHeight: "1.2em",
              maxHeight: "2.4em",
            }}
          >
            {params.row?.natMeasureInstitutionAbbreviation ?? "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      field: "natMeasureDeadline",
      headerName: "Рок за реализацију",
      width: 120,
      renderCell: (params) =>
        renderTruncated(params.value ?? params.row?.natMeasureDeadline),
    },
    {
      field: "status",
      headerName: "Статус реализације",
      width: 160,

      renderCell: (params) => {
        return (
          <div className="flex flex-row justify-between">
            {renderTruncated(
              params.value && params.value === "Није реализовано"
                ? "-"
                : params.value,
            )}
            {canUserEditRow(params.row) && <FaPencilAlt className="ml-2" />}
          </div>
        );
      },
    },
    {
      field: "natMeasureAdopByGovDate",
      headerName: "Датум реализације у владиним процедурама",
      minWidth: 180,

      renderCell: (params) => {
        const v = params.row?.natMeasureAdopByGovDate;

        if (!v && v !== 0) {
          return (
            <div className="flex flex-row items-center justify-between w-full">
              {renderTruncated("-")}
              {canUserEditRow(params.row) && <FaPencilAlt className="ml-2" />}
            </div>
          );
        }

        return (
          <div className="flex flex-row items-center justify-between w-full">
            {renderTruncated(String(v))}
            {canUserEditRow(params.row) && <FaPencilAlt className="ml-2" />}
          </div>
        );
      },
    },
    {
      field: "natMeasureCelex",
      headerName: "Веза са европским прописом (CELEX)",
      flex: 1,

      renderCell: (params) => (
        <div>{renderTruncated(params.row?.natMeasureCelex ?? "")}</div>
      ),
    },
    {
      field: "natMeasurePirvId",
      headerName: "Веза са планом рада владе (PIRV)",
      flex: 1,

      renderCell: (params) => (
        <div>
          {renderTruncated(
            params.row?.natMeasurePirvId !== ""
              ? params.row?.natMeasurePirvId
              : "-",
          )}
        </div>
      ),
    },
    {
      field: "natMeasureNegotiationDocs",
      headerName: "Веза са преговарачким документом",
      flex: 1,

      renderCell: (params) => (
        <div>
          {renderTruncated(params.row?.natMeasureNegotiationDocs ?? "-")}
        </div>
      ),
    },
    {
      field: "natMeasureStrategyDocs",
      headerName: "Веза са стратешким документом",
      flex: 1,

      renderCell: (params) => (
        <div>{renderTruncated(params.row?.natMeasureStrategyDocs ?? "-")}</div>
      ),
    },
    {
      field: "natMeasureOj",
      headerName: "Службени гласник",
      width: 120,

      renderCell: (params) => (
        <div className="flex flex-row items-center justify-between w-full">
          {renderTruncated(params.value ?? params.row?.natMeasureOj)}
          {canUserEditRow(params.row) && <FaPencilAlt className="ml-2 " />}
        </div>
      ),
    },

    {
      field: "natMeasureNote",
      headerName: "Напомена",
      minWidth: 130,

      renderCell: (params) => {
        const v =
          params.row?.natMeasureNote !== "" ? params.row?.natMeasureNote : "-";
        if (v === null || v === undefined) return renderTruncated("-");
        if (typeof v === "object") return renderTruncated(JSON.stringify(v));
        return (
          <div className="flex flex-row items-center justify-between w-full">
            {renderTruncated(String(v))}
            {canUserEditRow(params.row) && (
              <FaPencilAlt className="ml-2" height={20} width={20} />
            )}
          </div>
        );
      },
    },
  ];

  const fetchRegisters = async () => {
    setIsLoading(true);
    try {
      const limit = Number(pageSize);
      const offset = Number(page * pageSize);
      const searchString = term || "";
      const institutionId =
        defaultInstitution?.id ?? defaultInstitution?.value ?? undefined;

      const sortField =
        sortModel && sortModel.length > 0 ? sortModel[0].field : undefined;
      const sortOrder =
        sortModel && sortModel.length > 0 ? sortModel[0].sort : undefined; // "asc" | "desc"

      let resp = await getNationalMeasureReportingByQuarter(
        reportId,
        limit,
        offset,
        searchString,
        institutionId,
        status,
        sortField,
        sortOrder,
      );

      if (resp?.status === 201) {
        const data = resp.data ?? {};
        const items =
          data.regulationReports ?? data?.regulationReports ?? data ?? [];
        const total = data.totalCount;
        setEditDisabled(!data.status);

        setRegisters(items);
        setRowCountState(total);
        setCountsForFilter({
          totalPlannedCount: data.totalPlannedCount ?? 0,
          unrealisedCount: data.totalUnrealisedCount ?? 0,
        });
        setSelectedQuarter(data.quarter || "");
      } else {
        notif("Грешка при учитавању списка прописа", "error");
        setRegisters([]);
        setRowCountState(0);
      }
    } catch (err) {
      notif("Грешка при учитавању списка прописа", "error");
      setRegisters([]);
      setRowCountState(0);
    } finally {
      setIsLoading(false);
    }
  };

  const setPageAndCall = (newPage: number) => {
    setPage(newPage);
  };

  const handleSortChange = (model: GridSortModel) => {
    setPage(0);
    setSortModel(model);
  };

  const canUserEditRow = (row: any) => {
    if (editDisabled && !isAdmin) return false;
    if (isAdmin) return true;
    if (isCoordinator && row?.institutionId === user?.institution.id) {
      return true;
    }
    if (isDataEntryOrSecretary && row?.userId === user?.id) return true;
    return false;
  };

  const downloadWord = async () => {
    setFileLoading(true);

    const institutionId =
      defaultInstitution?.id ?? defaultInstitution?.value ?? null;
    const searchString = term || "";

    const downloadIfOk = (resp: any, label: string) => {
      if (resp?.status === 200) {
        download(resp?.data?.data, resp?.data?.name, resp?.data?.type);
        return true;
      }

      notif(`Грешка при преузимању документа (${label}).`, "error");
      return false;
    };

    try {
      const statusesToDownload: Array<"planned" | "unrealised"> =
        status === "" ? ["planned", "unrealised"] : [status];

      let success = false;

      if (selectedReportType === "institution") {
        for (const currentStatus of statusesToDownload) {
          try {
            const resp = await getInstitutionCelexFileNew(
              institutionId || "",
              selectedQuarter,
              selectedQuarter,
              "",
              true,
              searchString,
              reportId,
              currentStatus,
            );

            success =
              downloadIfOk(resp, `institution-${currentStatus}`) || success;
          } catch {
            notif(
              `Грешка при преузимању документа (institution-${currentStatus}).`,
              "error",
            );
          }
        }

        if (!success) {
          notif("Ниједан документ није успешно преузет.", "error");
        }
        return;
      }

      try {
        const resp = await getClusterFileNew(
          institutionId || "",
          selectedQuarter,
          selectedQuarter,
          "",
          true,
          searchString,
          reportId,
          status,
        );

        if (!downloadIfOk(resp, "cluster")) {
          notif("Документ није успешно преузет.", "error");
        }
      } catch {
        notif("Грешка при преузимању документа (cluster).", "error");
      }
    } finally {
      setFileLoading(false);
    }
  };

  return (
    <div
      className="py-10 px-6 overflow-auto gray-small-scroll h-full flex flex-col"
      ref={scrollRef}
    >
      <div className="flex gap-6 items-center justify-between pr-10">
        <h1 className="text-[#04438b] text-4xl font-bold">
          Извештавање о прописима {selectedQuarter && `- ${selectedQuarter}`}
        </h1>
        <HelpButton onClick={() => setHelpModal(true)} />
      </div>
      <div className="mt-6">
        {counts ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {Object.entries(counts)
              .filter(([key]) => key !== "quarter")
              .map(([key, value]) => (
                <Counter
                  key={key}
                  name={key}
                  value={value}
                  quarter={selectedQuarter}
                />
              ))}
          </div>
        ) : (
          <div className="text-gray-500">No data</div>
        )}
      </div>
      <div>
        <FilterForm
          term={term}
          setTerm={setTerm}
          defaultInstitution={defaultInstitution}
          onInstitutionChange={handleInstitutionChange}
          totalPlannedCount={countsForFilter.totalPlannedCount}
          unrealisedCount={countsForFilter.unrealisedCount}
          status={status}
          setStatus={setStatus}
          setReportType={setSelectedReportType}
          reportType={selectedReportType}
          downloadWord={downloadWord}
          quarter={selectedQuarter}
        />
      </div>
      <div className="mt-5 min-h-[430px] w-full">
        <DataGrid
          rowCount={rowCountState}
          loading={isLoading}
          pagination
          disableColumnMenu
          paginationMode="server"
          onPageChange={(newPage) => setPageAndCall(newPage)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(0);
          }}
          sortModel={sortModel}
          page={page}
          pageSize={pageSize}
          rows={registers}
          onCellClick={handleCellClick}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight={true}
          rowHeight={52}
          style={{ zIndex: 0 }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          sortingMode="server"
          onSortModelChange={(model) => handleSortChange(model)}
          getRowClassName={(params) => {
            const out = params?.row?.outOfForce;
            const pirv = params?.row?.pirv;
            return out ? "redRow" : pirv ? "blueRow" : "";
          }}
          getCellClassName={(params) => {
            const editableFields = new Set([
              "status",
              "natMeasureAdopByGovDate",
              "vezaEvropskiCelex",
              "vezaPregovaracki",
              "vezaStrateski",
              "vezaPirv",
              "natMeasureNote",
              "natMeasureOj",
            ]);

            if (!canUserEditRow(params.row)) return "";

            if (editableFields.has(params.field)) {
              return "editable-cell";
            }
            return "";
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: "1.2em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            },
            "& .MuiDataGrid-columnHeader": {
              height: "auto !important",
              minHeight: "56px !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              maxHeight: "100px !important",
            },
            "& .editable-cell": {
              cursor: editDisabled ? "default" : "pointer",
              padding: "0 !important",
              transition: "background-color 0.2s ease",
              paddingRight: "8px !important",
              paddingLeft: "8px !important",
              ...(!editDisabled && {
                "&:hover": {
                  backgroundColor: "#242EBF",
                  textDecoration: "underline",
                  color: "white",
                },
              }),
            },
          }}
          components={{
            Footer: CustomTableFooter,
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Не постоје прописи са задатим критеријумом
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                Не постоје прописи са задатим критеријумом
              </Stack>
            ),
          }}
          componentsProps={{
            footer: {
              count: rowCountState,
              page: page,
              pageSize: pageSize,
              onPageChange: (event: any, newPage: any) =>
                setPageAndCall(newPage),
              onPageSizeChange: (newPageSize: any) => setPageSize(newPageSize),
              rowsPerPageOptions: [5, 10, 20],
              loadRegisters: fetchRegisters,
            },
          }}
        />
      </div>
      {showEditRealisationModal && itemToChange && (
        <EditRealisationModal
          open={showEditRealisationModal}
          register={itemToChange}
          onClose={() => {
            setShowEditRealisationModal(false);
            setItemToChange(null);
            fetchCounts();
            fetchRegisters();
          }}
          closeModal={() => {
            setShowEditRealisationModal(false);
          }}
          quarter={selectedQuarter}
          setTempRealisation={setTempRealisation}
          callback={() => setShowOjModal(true)}
          setNoteData={setNoteData}
          disabled={editDisabled}
        />
      )}
      {showNegDocumentLinkModal && itemToChange && (
        <NegDocsModal
          open={showNegDocumentLinkModal}
          register={itemToChange}
          onClose={() => {
            setShowNegDocumentLinkModal(false);
            fetchRegisters();
          }}
        />
      )}
      {showStrategicDocumentLinkModal && itemToChange && (
        <StrategicDocsModal
          open={showStrategicDocumentLinkModal}
          register={itemToChange}
          onClose={() => {
            setShowStrategicDocumentLinkModal(false);
            fetchRegisters();
          }}
        />
      )}
      {showPirvDocumentLinkModal && itemToChange && (
        <ShowPirvDocumentModal
          open={showPirvDocumentLinkModal}
          register={itemToChange}
          onClose={() => {
            setShowPirvDocumentLinkModal(false);
          }}
        />
      )}
      {showEditNoteModal && itemToChange && (
        <EditNoteModal
          open={showEditNoteModal}
          item={itemToChange}
          onClose={() => {
            setShowEditNoteModal(false);
            setItemToChange(null);
            fetchRegisters();
          }}
          onSave={handleSaveNote}
          disabled={!canUserEditRow(itemToChange)}
        />
      )}
      {showOjModal && itemToChange && (
        <OjModal
          open={showOjModal}
          register={itemToChange}
          onClose={() => {
            setShowOjModal(false);
            fetchRegisters();
            fetchCounts();
          }}
          tempRealisation={tempRealisation}
          noteData={noteData}
          disabled={!canUserEditRow(itemToChange)}
          quarter={selectedQuarter}
        />
      )}
      {showEURelationModal && itemToChange && (
        <EuRelationModal
          open={showEURelationModal}
          register={itemToChange}
          onClose={() => {
            setShowEURelationModal(false);
            fetchRegisters();
          }}
        />
      )}
      <HelpPageModal
        modalIsOpen={helpModal}
        closeModal={() => {
          setHelpModal(false);
        }}
        name={"reportingNational"}
      />
    </div>
  );
};

export default NationalMeasuresReport;
